import { useState, useContext, useRef } from "react";
import { AuthContext, API } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Upload, FileText, CheckCircle, AlertCircle, Info } from "lucide-react";

export default function ImportPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error("Bitte wählen Sie eine CSV-Datei aus");
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setShowConfirmDialog(false);
    setIsImporting(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/articles/import-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult(response.data);
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`Import abgeschlossen mit ${response.data.errors.length} Warnungen`);
      } else {
        toast.success(`Import erfolgreich: ${response.data.imported} neu, ${response.data.updated} aktualisiert`);
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Importieren der CSV");
      setImportResult({
        message: "Import fehlgeschlagen",
        imported: 0,
        updated: 0,
        errors: [error.response?.data?.detail || "Unbekannter Fehler"]
      });
    } finally {
      setIsImporting(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Zugriff verweigert</CardTitle>
            <CardDescription>
              Nur Administratoren haben Zugriff auf die Import-Funktion.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/")} className="w-full">
              Zurück zum Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                size="icon"
                className="border-slate-300"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">CSV Import</h1>
                  <p className="text-sm text-slate-600">Artikel aus CSV-Datei importieren</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid gap-6">
          {/* Upload Card */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Upload className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">CSV-Datei hochladen</CardTitle>
                  <CardDescription className="text-sm">
                    Wählen Sie eine CSV-Datei zum Importieren aus
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isImporting}
                  className="mb-2"
                >
                  CSV-Datei auswählen
                </Button>
                {selectedFile && (
                  <p className="text-sm text-slate-600 mt-2">
                    Ausgewählt: <strong>{selectedFile.name}</strong>
                  </p>
                )}
              </div>

              {selectedFile && (
                <Button
                  onClick={() => setShowConfirmDialog(true)}
                  disabled={isImporting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
                >
                  {isImporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Import läuft...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Artikel importieren
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Import Result */}
          {importResult && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {importResult.errors && importResult.errors.length > 0 ? (
                    <>
                      <AlertCircle className="w-5 h-5 text-orange-600" />
                      Import Ergebnis (mit Warnungen)
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      Import Erfolgreich
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-700 font-medium">Neu importiert</p>
                    <p className="text-2xl font-bold text-green-900">{importResult.imported}</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-700 font-medium">Aktualisiert</p>
                    <p className="text-2xl font-bold text-blue-900">{importResult.updated}</p>
                  </div>
                </div>

                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-medium text-orange-900 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Warnungen ({importResult.errors.length})
                    </h4>
                    <ul className="text-sm text-orange-700 space-y-1">
                      {importResult.errors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <Button
                  onClick={() => navigate("/articles")}
                  variant="outline"
                  className="w-full"
                >
                  Artikel anzeigen
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Information Card */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                CSV-Format Anforderungen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-slate-600">
                <div>
                  <h4 className="font-medium text-slate-900 mb-1">📋 Erforderliche Spalten</h4>
                  <p className="mb-2">
                    Die CSV-Datei muss folgende Spalten enthalten (mit Semikolon getrennt):
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><strong>artikelnummer</strong> - Eindeutige Artikelnummer</li>
                    <li><strong>Artikelname</strong> - Name des Artikels (erforderlich)</li>
                    <li><strong>Beschreibung</strong> - Detaillierte Beschreibung</li>
                    <li><strong>gewicht</strong> - Gewicht in kg (Dezimalzahl)</li>
                    <li><strong>einheit</strong> - Mengeneinheit (z.B. Stück, Liter, Set)</li>
                    <li><strong>preis</strong> - Preis (netto)</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-slate-900 mb-1">🔄 Import-Verhalten</h4>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Artikel mit existierender Artikelnummer werden aktualisiert</li>
                    <li>Neue Artikel werden automatisch erstellt</li>
                    <li>Dezimalzahlen können mit Komma (,) oder Punkt (.) formatiert sein</li>
                    <li>Leere Felder werden als null gespeichert</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-slate-900 mb-1">📝 Beispiel-Zeile</h4>
                  <div className="bg-slate-100 rounded p-2 font-mono text-xs overflow-x-auto">
                    ARTIKEL123;Beispiel Artikel;Produkt Beschreibung;2.5;Stück;49,90
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Confirm Import Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              CSV-Import starten?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 pt-2">
              <p className="font-medium text-slate-900">
                Möchten Sie die Artikel aus dieser CSV-Datei importieren?
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-sm text-blue-700">
                  <strong>Hinweis:</strong> Artikel mit existierender Artikelnummer werden 
                  aktualisiert. Neue Artikel werden hinzugefügt. Bilder und Kategorien 
                  bleiben bei bestehenden Artikeln erhalten.
                </p>
              </div>
              {selectedFile && (
                <p className="text-sm text-slate-600">
                  <strong>Datei:</strong> {selectedFile.name}
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowConfirmDialog(false)}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleImport}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Ja, importieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
